import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@inboundx/shared/config';

import type { FunnelStage } from '@/utils/funnel';
import type { IntentScore } from '@/utils/intent';
import { getLanguageFlag } from '@/utils/languageFlags';

export type DateRangeFilter =
    | '1hour'
    | 'today'
    | '24hours'
    | '7days'
    | '14days'
    | '30days'
    | '90days'
    | 'all'
    | 'custom';

export interface CustomDateRange {
    from: Date;
    to: Date;
}

export const DATE_RANGE_OPTIONS: Exclude<DateRangeFilter, 'custom'>[] = [
    '1hour',
    'today',
    '24hours',
    '7days',
    '14days',
    '30days',
    '90days',
    'all',
];

// `playground`, `webmcp` and `agent_http` are not real `conversations.environment`
// values — those chats are stamped `production` and tagged by `deployment_target`.
// They ride here because the environment filter is the list's only origin control
// (IX-4845, IX-5319, IX-5425).
export type Origin = 'playground' | 'webmcp' | 'agent_http' | 'connectors';
export type Environment = 'production' | 'test' | 'staging' | Origin;

export function isOrigin(env: Environment): env is Origin {
    return env === 'playground' || env === 'webmcp' || env === 'agent_http' || env === 'connectors';
}

export function isR4aSource(source: string | null | undefined): boolean {
    return source?.split(':')[0] === 'r4a';
}

/** A WebMCP agent turn: `webmcp:<tool>`, or bare `webmcp` before the tool was recorded. */
export function isWebmcpSource(source: string | null | undefined): boolean {
    return source?.split(':')[0] === 'webmcp';
}

/** An Agent HTTP endpoint turn (`agent_http:ask`), asked over plain HTTP (IX-5425). */
export function isAgentHttpSource(source: string | null | undefined): boolean {
    return source?.split(':')[0] === 'agent_http';
}

// Empty array means "all environments" (no filter applied).
export type EnvironmentFilter = Environment;

export interface EnvironmentOption {
    value: Environment;
    label: string;
    adminOnly?: boolean;
}

/**
 * Base set — every surface with an environment control. Excludes `playground`,
 * which is an origin rather than an environment and is only honoured by the
 * Conversations list.
 */
export const ENVIRONMENT_OPTIONS: EnvironmentOption[] = [
    { value: 'production', label: 'Production' },
    { value: 'test', label: 'Test', adminOnly: true },
    { value: 'staging', label: 'Staging', adminOnly: true },
];

/**
 * For surfaces that filter conversations by origin — only the Conversations
 * list. Visitors and Accounts run through RPCs with no `deployment_target`
 * predicate, so offering Playground there would be a control that silently
 * does nothing.
 */
export const ENVIRONMENT_OPTIONS_WITH_ORIGIN: EnvironmentOption[] = [
    ...ENVIRONMENT_OPTIONS,
    { value: 'playground', label: 'Playground' },
    // AI-agent tool calls, kept out of client stats and staff-only for now (IX-5319).
    { value: 'webmcp', label: 'AI agents (WebMCP)', adminOnly: true },
    { value: 'agent_http', label: 'AI agents (HTTP)', adminOnly: true },
    { value: 'connectors', label: 'AI agents (MCP / R4A)', adminOnly: true },
];

/**
 * What a non-staff user may filter on. Derived from the option list so the two
 * can't drift — a new client-visible env is one missing `adminOnly`.
 */
export const CLIENT_ENVIRONMENTS: EnvironmentFilter[] = ENVIRONMENT_OPTIONS_WITH_ORIGIN.filter(
    (opt) => !opt.adminOnly
).map((opt) => opt.value);

export type FunnelStageFilter = FunnelStage | 'none';

export const FUNNEL_STAGE_OPTIONS: { value: FunnelStageFilter; label: string }[] = [
    { value: 'none', label: 'Conversation started' },
    { value: 'Conversion offered', label: 'Conversion offered' },
    { value: 'Conversion initiated', label: 'Conversion initiated' },
    { value: 'Conversion completed', label: 'Conversion completed' },
];

export type InitialQuestionType =
    | 'user_typed'
    | 'dynamic_question'
    | 'ai_section'
    | 'ai_section_button'
    | 'ai_section_search_bar';

export const INITIAL_QUESTION_TYPE_OPTIONS: { value: InitialQuestionType; label: string }[] = [
    { value: 'user_typed', label: 'Visitor typing' },
    { value: 'dynamic_question', label: 'Dynamic question' },
    { value: 'ai_section', label: 'AI Section (all)' },
    { value: 'ai_section_button', label: 'AI Section (button)' },
    { value: 'ai_section_search_bar', label: 'AI Section (search bar)' },
];

/**
 * Conversation language filter — restricted to the widget's supported languages
 * (`SUPPORTED_LANGUAGES`). Filter values are ISO codes (used in the `?language=`
 * URL param). "auto-detect" / null conversations are intentionally not selectable.
 */
export type ConversationLanguage = SupportedLanguage;

/**
 * The `conversations.conversation_language` column stores capitalized ENGLISH
 * names (French, German, ...), distinct from `LANGUAGE_NAMES` which are native
 * (Français, Deutsch). This one map is both the option label and the DB value we
 * match against. `Record<SupportedLanguage>` forces a compile error if a new
 * supported language is added without its English name here.
 */
const LANGUAGE_ENGLISH_NAME: Record<SupportedLanguage, string> = {
    en: 'English',
    fr: 'French',
    de: 'German',
    es: 'Spanish',
    it: 'Italian',
    pt: 'Portuguese',
    bg: 'Bulgarian',
    nl: 'Dutch',
    ro: 'Romanian',
    hu: 'Hungarian',
};

export const CONVERSATION_LANGUAGE_OPTIONS: { value: ConversationLanguage; label: string }[] =
    SUPPORTED_LANGUAGES.map((code) => ({
        value: code,
        label: `${getLanguageFlag(code)} ${LANGUAGE_ENGLISH_NAME[code]}`.trim(),
    }));

/** Map ISO codes to the DB `conversation_language` values for a server-side `.in()`. */
export const languageCodesToDbValues = (codes: ConversationLanguage[]): string[] =>
    codes.map((c) => LANGUAGE_ENGLISH_NAME[c]);

const DB_VALUE_TO_LANGUAGE_CODE = Object.fromEntries(
    (Object.entries(LANGUAGE_ENGLISH_NAME) as [ConversationLanguage, string][]).map(
        ([code, name]) => [name, code]
    )
) as Record<string, ConversationLanguage>;

/** Reverse of {@link languageCodesToDbValues}: DB `conversation_language` → ISO code (null if unsupported). */
export const dbValueToLanguageCode = (name: string): ConversationLanguage | null =>
    DB_VALUE_TO_LANGUAGE_CODE[name] ?? null;

export type MinTurns = 1 | 2 | 3 | 4 | 5 | 6;

export type IntentFilter = IntentScore | 'all';

export const INTENT_FILTER_LABELS: Record<IntentScore, string> = {
    1: 'Cold',
    2: '2+ Curious',
    3: '3+ Interested',
    4: '4+ Engaged',
    5: '5 Hot',
};

export type MinMessages = 0 | 1 | 5 | 10;

export const MIN_MESSAGE_OPTIONS: { value: MinMessages; label: string }[] = [
    { value: 0, label: 'All visitors' },
    { value: 1, label: 'Chatting only' },
    { value: 5, label: '5+ messages' },
    { value: 10, label: '10+ messages' },
];

export type SortOption = 'latest' | 'messages' | 'name' | 'intent' | 'lastActivity';
/** The Conversations list sorts in the database only, by When or Turns. */
export type ConversationSortOption = Extract<SortOption, 'latest' | 'messages'>;
export type SortDirection = 'asc' | 'desc';

export type AttentionFilter = 'needs_attention' | 'has_unknowns';

export const ATTENTION_OPTIONS: { value: AttentionFilter; label: string }[] = [
    { value: 'needs_attention', label: 'Needs attention' },
    { value: 'has_unknowns', label: 'Has unknowns' },
];

/**
 * Whether to restrict the Visitors page to visitors with at least one
 * identification provider row (any of Vector, Snitcher, Enrich.so, ...).
 * Backed by RPC `get_visitor_stats_with_intent(p_identified_only := true)`.
 */
export type IdentifiedFilter = boolean;
